<script lang="ts">
  import { onMount } from "svelte"
  import { mdiArrowLeft, mdiCalendarCheck, mdiCalendarSearch, mdiClose } from "@mdi/js"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import {
    fetchAppointmentSlotRecommendations,
    type PublicAppointmentSlot,
  } from "../../../../../../../../api/public/appointment-portal"
  import RecommendationCalendarNoticeOverlay from "./_modules/recommendation-calendar-notice-overlay"
  import SlotSummary from "../../_shared/_modules/slot-summary"
  import TimeSlotList from "../../_shared/_modules/time-slot-list"

  const MAX_RECOMMENDATIONS = 6

  const isAbortError = (err: unknown) =>
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name?: string }).name === "AbortError"

  let {
    orgIdentifier,
    code,
    busy = false,
    onbook,
    onback,
    onfallbackcalendar,
  }: {
    orgIdentifier: string
    code: string
    busy?: boolean
    onbook: (slot: PublicAppointmentSlot) => Promise<boolean>
    onback: () => void
    onfallbackcalendar: () => void
  } = $props()

  let loading = $state(true)
  let slots = $state<PublicAppointmentSlot[]>([])
  let selectedSlot = $state<PublicAppointmentSlot | null>(null)
  let abortController: AbortController | null = null

  const loadRecommendations = async () => {
    abortController?.abort()
    const controller = new AbortController()
    abortController = controller
    loading = true
    selectedSlot = null
    try {
      const response = await fetchAppointmentSlotRecommendations(orgIdentifier, code, {
        signal: controller.signal,
      })

      if (abortController !== controller) return

      slots = response.slots.slice(0, MAX_RECOMMENDATIONS)

      if (!slots.length) onfallbackcalendar()
    } catch (err) {
      if (isAbortError(err)) return

      console.error("appointment_slot_recommendations_failed", err)

      if (abortController === controller) onfallbackcalendar()
    } finally {
      if (abortController === controller) {
        loading = false
        abortController = null
      }
    }
  }

  const openCalendarNotice = () => {
    openOverlay({
      title: "Andere Termine anzeigen?",
      content: RecommendationCalendarNoticeOverlay,
      defaultMobileFullscreen: true,
      properties: {
        onshowcalendar: onfallbackcalendar,
      },
    })
  }

  const confirmSelectedSlot = async () => {
    if (!selectedSlot) return

    const success = await onbook(selectedSlot)

    if (!success) selectedSlot = null
  }

  onMount(() => {
    void loadRecommendations()

    return () => abortController?.abort()
  })
</script>

<SectionTitle
  title="Passenden Alternativtermin auswählen"
  small={true}
  brightBackground={true}
  subtitle="Termin ändern"
  styleOuter="margin-bottom: 1.2rem;"
>
  {#snippet buttonSnippet()}
    <CTA
      type="tertiaryButton"
      icon={mdiArrowLeft}
      content="Zurück"
      darkMode={false}
      onClick={onback}
    />
  {/snippet}
</SectionTitle>

<p class="recommendation-hint">
  Diese Termine passen besonders gut in die bestehende Tour. Wählen Sie nach Möglichkeit einen der
  vorgeschlagenen Termine aus.
</p>

<div class="recommendation-grid">
  <div class="recommendation-options">
    <TimeSlotList
      {slots}
      {selectedSlot}
      title="Empfohlene Termine"
      showDate={true}
      {loading}
      dateSelected={true}
      mobileVisibleCount={MAX_RECOMMENDATIONS}
      mobileBatchSize={MAX_RECOMMENDATIONS}
      onselect={(slot) => (selectedSlot = slot)}
      onretry={() => undefined}
      onshowmore={() => undefined}
    />
    {#if !loading && slots.length}
      <div class="calendar-fallback">
        <CTA
          type="tertiaryButton"
          icon={mdiCalendarSearch}
          content="Keiner dieser Termine passt"
          darkMode={false}
          disabled={busy}
          onClick={openCalendarNotice}
        />
      </div>
    {/if}
  </div>
  <div class="decision-panel">
    <p class="decision-panel-title">Zusammenfassung</p>
    <SlotSummary slot={selectedSlot} />
    <div class="actions">
      <span class="action-control action-control--cancel">
        <CTA
          type="tertiaryButton"
          icon={mdiClose}
          content="Abbrechen"
          ariaLabel="Abbrechen"
          darkMode={false}
          onClick={onback}
        />
      </span>
      <span class="action-control action-control--confirm" class:action-control--busy={busy}>
        <CTA
          type="primaryButton"
          icon={mdiCalendarCheck}
          content={busy ? "Wird gebucht" : "Auswahl bestätigen"}
          ariaLabel={busy ? "Wird gebucht" : "Auswahl bestätigen"}
          darkMode={false}
          disabled={!selectedSlot || busy || loading}
          onClick={confirmSelectedSlot}
          fullWidth={true}
        />
      </span>
    </div>
  </div>
</div>

<style>
  .recommendation-hint {
    margin: 0 0 1.2rem;
    color: var(--portal-muted, #42514f);
  }

  .recommendation-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(20rem, 1fr);
    gap: 1.25rem;
    align-items: start;
    width: 100%;
    min-width: 0;
  }

  .decision-panel {
    display: grid;
    gap: 0.9rem;
    align-content: start;
    width: 100%;
    min-width: 0;
  }

  .recommendation-options {
    display: grid;
    gap: 1rem;
    width: 100%;
    min-width: 0;
  }

  .calendar-fallback {
    display: flex;
    justify-content: flex-start;
  }

  .decision-panel-title {
    margin: 0;
    color: var(--portal-ink, var(--bg-100));
    font-weight: 800;
  }

  .actions {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 0.7rem;
    justify-content: flex-end;
  }

  .actions :global(.cta) {
    min-width: 0;
    padding-inline: 0.85rem;
    font-size: 0.9rem;
  }

  .action-control {
    display: contents;
  }

  .actions :global(.cta.fullWidth) {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
  }

  @media (max-width: 900px) {
    .recommendation-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .decision-panel-title {
      display: none;
    }
  }

  @media (max-width: 700px) {
    .actions {
      justify-content: stretch;
    }

    .calendar-fallback :global(.cta) {
      width: 100%;
    }
  }

  @media (max-width: 560px) {
    .action-control--cancel :global(.cta p) {
      display: none;
    }

    .action-control--confirm :global(.cta p) {
      font-size: 0;
    }

    .action-control--confirm :global(.cta p)::after {
      content: "Bestätigen";
      font-size: 0.9rem;
    }

    .action-control--confirm.action-control--busy :global(.cta p)::after {
      content: "Wird gebucht";
    }
  }
</style>
